// Patients: backend rows → the design's vocabulary (state/patients.tsx), plus
// the small parts the Patients screens share that our shared kits don't have
// (sortRows, the appointment status badge). Used by Patients, PatientView,
// PatientForm and the treatment plan screens.
import { fetchAllPages } from "../crm/contactsKit";
import { localDay, toAppointment } from "../../lib/home";
import { DAY, ymd } from "../../lib/format";

/* ---------- Sorting (design: components/crm/ui.tsx sortRows) ---------- */

/** Sort rows by the column whose `sort` is a value function; `sort` = { id, dir } or null. */
export function sortRows(rows, columns, sort) {
  if (!sort) return rows;
  const col = columns.find((c) => c.id === sort.id);
  if (!col || typeof col.sort !== "function") return rows;
  return [...rows].sort((a, b) => {
    const x = col.sort(a);
    const y = col.sort(b);
    if (typeof x === "number" && typeof y === "number") return (x - y) * sort.dir;
    return String(x ?? "").localeCompare(String(y ?? "")) * sort.dir;
  });
}

/* ---------- Patients ---------- */

export const GENDERS = ["Female", "Male", "Other", "Prefer not to say"];
const GENDER_LABEL = { female: "Female", male: "Male", other: "Other", prefer_not_to_say: "Prefer not to say" };
export const GENDER_KEY = { Female: "female", Male: "male", Other: "other", "Prefer not to say": "prefer_not_to_say" };
/** patients.marital_status (old PatientForm) → label. */
export const MARITAL = [["single", "Single"], ["married", "Married"], ["divorced", "Divorced"], ["widowed", "Widowed"]];
export const CHANNELS = ["WhatsApp", "Call", "SMS", "Email"];
const CHANNEL_LABEL = { whatsapp: "WhatsApp", phone: "Call", sms: "SMS", email: "Email" };
export const CHANNEL_KEY = { WhatsApp: "whatsapp", Call: "phone", SMS: "sms", Email: "email" };

const list = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [String(parsed)]; } catch (_) { return [value]; }
  }
  return [];
};
const obj = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") { try { const v = JSON.parse(value); return v && typeof v === "object" ? v : {}; } catch (_) { return {}; } }
  return {};
};
/** "2026-10-27" from a DATE the backend sent (plain or ISO). */
export const isoDate = (value) => { const d = localDay(value); return d === null ? "" : ymd(d); };
const digits10 = (p) => String(p || "").replace(/\D/g, "").slice(-10);
const num = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/** /api/patients row → the design's Patient (with its contact fields inline). */
export function toPatient(row) {
  const custom = obj(row.custom_fields);
  return {
    id: row.id,
    code: row.patient_id_number || `#${row.id}`,
    contactId: row.contact_id || null,
    facilityId: row.facility_id || null,
    facilityName: row.facility_name || "",
    // Mr, Ms, Dr… on the patient (item 189); null until set.
    title: row.title || "",
    firstName: row.first_name || "",
    lastName: row.last_name || "",
    phone: digits10(row.phone),
    altPhone: digits10(row.alternate_phone),
    email: row.email || "",
    address: { line1: row.address_line1 || "", line2: row.address_line2 || "", city: row.city || "", state: row.state || "", pin: row.pincode || "", country: row.country || "" },
    dob: isoDate(row.date_of_birth),
    ageValue: row.age === null || row.age === undefined ? null : Number(row.age),
    gender: GENDER_LABEL[row.gender] || "",
    bloodGroup: row.blood_group || "",
    emergency: { name: row.emergency_contact_name || "", phone: digits10(row.emergency_contact_phone), relationship: row.emergency_contact_relation || "" },
    medical: {
      allergies: list(row.allergies),
      conditions: list(row.chronic_conditions),
      medications: list(row.current_medications).join("\n"),
      pastTreatments: list(row.medical_history).join("\n"),
      notes: typeof custom.medical_notes === "string" ? custom.medical_notes : "",
    },
    insurance: row.insurance_provider || row.insurance_policy_number
      ? { provider: row.insurance_provider || "", policyNumber: row.insurance_policy_number || "", holderName: custom.insurance_holder_name || "", relationship: custom.insurance_relationship || "Self", validTill: isoDate(row.insurance_expiry_date) }
      : null,
    preferences: {
      doctorId: row.preferred_doctor_id || "",
      doctor: row.preferred_doctor_name || "",
      facilityId: row.preferred_facility_id || "",
      facility: row.preferred_facility_name || "",
      language: row.preferred_language || "English",
      channel: CHANNEL_LABEL[row.preferred_communication] || "Call",
    },
    // Consents (item 188). null when the row doesn't carry them (migration 184 not applied).
    whatsappReminders: row.whatsapp_reminders === undefined || row.whatsapp_reminders === null ? null : Number(row.whatsapp_reminders) !== 0,
    marketingOptIn: row.marketing_opt_in === undefined || row.marketing_opt_in === null ? null : Number(row.marketing_opt_in) !== 0,
    notes: row.notes || "",
    // Old PatientForm fields: marital status and staff-only notes.
    maritalStatus: row.marital_status || "",
    internalNotes: row.internal_notes || "",
    // Visit statistics the backend keeps on the patient (old PatientDetails "Visit Statistics").
    stats: {
      appointments: num(row.total_appointments),
      visits: num(row.total_visits),
      billed: num(row.total_amount_billed),
      outstanding: num(row.outstanding_balance),
    },
    status: row.status === "active" ? "Active" : "Inactive",
    lastVisit: localDay(row.last_visit_date),
    createdAt: row.created_at ? new Date(row.created_at).getTime() : null,
    custom,
  };
}

export const patientName = (p) => `${p?.firstName || ""} ${p?.lastName || ""}`.trim();
export const patientInitials = (p) => `${(p?.firstName || "")[0] || ""}${(p?.lastName || "")[0] || ""}`.toUpperCase();

/** Age in years from "YYYY-MM-DD", else the stored age, else null. */
export function ageOf(dob, stored = null) {
  if (dob) {
    const [y, m, d] = dob.split("-").map(Number);
    const now = new Date();
    let a = now.getFullYear() - y;
    if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) a -= 1;
    return a >= 0 && a < 150 ? a : null;
  }
  return stored === null || Number.isNaN(stored) ? null : stored;
}

/* ---------- Appointments ---------- */

const TONE = {
  Booked: "bg-mist text-ink",
  Confirmed: "bg-paper text-ink ring-1 ring-ink/25",
  "Checked in": "bg-sky-soft text-ink",
  Completed: "bg-success-soft text-success",
  "No-show": "bg-warning-soft text-warning",
  Cancelled: "bg-mist text-muted",
};
const DOT = { Booked: "bg-muted/60", Confirmed: "bg-ink", "Checked in": "bg-sky-deep", Completed: "bg-success", "No-show": "bg-warning", Cancelled: "bg-muted" };

/** Design: components/appointments/AppointmentRow.tsx AppointmentStatusBadge. */
export function AppointmentStatusBadge({ status }) {
  return (
    <span className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-caption font-semibold ${TONE[status] || TONE.Booked}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[status] || DOT.Booked}`} />
      {status}
    </span>
  );
}

/** /api/appointments rows → visits (rescheduled halves dropped). */
export const toVisits = (rows) => (rows || []).map(toAppointment).filter((a) => a.status !== "Rescheduled" && a.start !== null);

/**
 * Appointments from `fromDay` onward for a location, all pages (GET /api/appointments).
 * Returns visits grouped by patient id, or null when the call fails.
 */
export async function visitsByPatient(fromDay, facilityId) {
  const rows = await fetchAllPages("/api/appointments", { date_from: ymd(fromDay), sort_by: "appointment_date", sort_order: "ASC", ...(facilityId ? { facility_id: facilityId } : {}) }, 20);
  if (!rows) return null;
  const map = new Map();
  toVisits(rows).forEach((a) => { const k = String(a.patientId); map.set(k, [...(map.get(k) || []), a]); });
  map.forEach((items) => items.sort((a, b) => a.start - b.start));
  return map;
}

/** The next booked visit (not cancelled / no-show) after `now`. */
export const nextVisit = (visits, now = Date.now()) => (visits || []).find((a) => a.start > now && a.status !== "Cancelled" && a.status !== "No-show");

export { DAY };
